import React, { useEffect, useState } from 'react';
import { Plus, Edit3, Power, X } from 'lucide-react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Card';
import { Input, Field } from '@/components/ui/Input';
import { useToast } from '@/components/ui/Toast';
import { errorMessage } from '@/lib/utils';

// Super-admin only: the platform-wide sport taxonomy every sport-picker
// (this app's court form, the customer venue/booking filters, the landing
// page search) reads from — a manageable list rather than a hardcoded
// enum, so growing past badminton never needs a code change, just a new
// row here. A sport is deactivated rather than deleted (see
// app.routers.sports.toggle_sport) so existing courts never end up
// pointing at a sport that no longer exists.
const empty = { name: '', icon: 'circle-dot', active: true };

export default function AdminSports() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const { toast } = useToast();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/sports');
      setRows(data);
    } catch (err) {
      toast({ title: 'Failed to load sports', description: errorMessage(err.response?.data?.detail), variant: 'error' });
    } finally {
      setLoading(false);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(empty); setEditing(null); setOpen(true); };
  const openEdit = (s) => { setForm({ name: s.name, icon: s.icon, active: s.active }); setEditing(s.id); setOpen(true); };

  const save = async () => {
    try {
      if (editing) await api.put(`/admin/sports/${editing}`, form);
      else await api.post('/admin/sports', form);
      toast({ title: editing ? 'Sport updated' : 'Sport added', variant: 'success' });
      setOpen(false);
      load();
    } catch (err) {
      toast({ title: 'Save failed', description: errorMessage(err.response?.data?.detail), variant: 'error' });
    }
  };

  const toggle = async (s) => {
    try {
      await api.put(`/admin/sports/${s.id}/toggle`);
      load();
    } catch {
      toast({ title: 'Failed', variant: 'error' });
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-sports">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-volt mb-2">Platform</div>
          <h1 className="font-heading font-black text-4xl uppercase tracking-tight">Sports</h1>
          <p className="text-sm text-white/50 mt-2 max-w-xl">
            The sports offered platform-wide. Deactivating one hides it from new court setup and customer search — existing courts keep it.
          </p>
        </div>
        <Button onClick={openNew} data-testid="add-sport-button"><Plus size={14} /> Add Sport</Button>
      </div>

      <div className="bg-surface border border-white/10 overflow-x-auto">
        <table className="w-full text-sm min-w-[600px]">
          <thead className="border-b border-white/10 bg-obsidian/40">
            <tr className="text-[10px] uppercase tracking-[0.25em] text-white/40">
              <th className="text-left px-5 py-3">Sport</th>
              <th className="text-left px-5 py-3">Courts</th>
              <th className="text-left px-5 py-3">Status</th>
              <th className="text-right px-5 py-3">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="4" className="px-5 py-12 text-center text-white/40">Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan="4" className="px-5 py-12 text-center text-white/40">No sports yet.</td></tr>
            ) : rows.map((s) => (
              <tr key={s.id} className="border-b border-white/5 hover:bg-white/5" data-testid={`sport-row-${s.id}`}>
                <td className="px-5 py-3 font-medium">{s.name}</td>
                <td className="px-5 py-3 font-mono text-white/70">{s.court_count ?? 0}</td>
                <td className="px-5 py-3"><Badge tone={s.active ? 'success' : 'default'}>{s.active ? 'Active' : 'Hidden'}</Badge></td>
                <td className="px-5 py-3 text-right">
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" size="sm" onClick={() => openEdit(s)} data-testid={`edit-sport-${s.id}`}><Edit3 size={12} /></Button>
                    <Button variant="outline" size="sm" onClick={() => toggle(s)} data-testid={`toggle-sport-${s.id}`}><Power size={12} /></Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-surface border border-white/10 max-w-md w-full p-6 sm:p-8" onClick={(e) => e.stopPropagation()} data-testid="sport-form-modal">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading font-bold text-2xl uppercase">{editing ? 'Edit' : 'New'} Sport</h2>
              <button onClick={() => setOpen(false)} className="text-white/40 hover:text-white"><X size={18} /></button>
            </div>
            <div className="space-y-4">
              <Field label="Name"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Cricket" data-testid="sport-name" /></Field>
              <Field label="Icon (lucide-react name)" hint="e.g. circle-dot, football, dribbble — falls back to a generic ball icon if unrecognized.">
                <Input value={form.icon} onChange={(e) => setForm({ ...form, icon: e.target.value })} placeholder="circle-dot" data-testid="sport-icon" />
              </Field>
              <label className="flex items-center gap-2 text-sm text-white/70 cursor-pointer">
                <input type="checkbox" checked={!!form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="accent-volt" />
                Active
              </label>
            </div>
            <div className="flex gap-3 mt-8">
              <Button variant="outline" onClick={() => setOpen(false)} className="flex-1">Cancel</Button>
              <Button onClick={save} className="flex-1" data-testid="save-sport">Save</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
